import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useNotification } from '../hooks/useNotification';
import axiosInstance from '../lib/axios';
import {
  ProviderNativeDefault,
  LLMModel,
  LLMModelFormValues,
  LLMModelModalityOption,
  LLMTaskTypeOption,
  LLMModelUsageScope,
  LLMProvider,
  LLMProviderFormValues,
  PricingDimension,
  UpdateLLMModelInput,
  UpdateLLMProviderInput,
  InvocationMethodDefinition,
} from '../types/admin';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  meta?: {
    total?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
  };
  message?: string;
  error?: string;
}

export const llmCatalogKeys = {
  providers: ['llm-providers'] as const,
  models: (filters?: Record<string, unknown>) => ['llm-models', filters ?? {}] as const,
  modalities: ['llm-model-modalities'] as const,
  taskTypes: ['llm-task-types'] as const,
  invocationMethods: ['llm-invocation-methods'] as const,
  pricingDimensions: (modelPublicId?: string) =>
    ['llm-model-pricing-dimensions', modelPublicId ?? ''] as const,
};

const parseList = <T>(response: ApiResponse<T[]>): T[] => response.data ?? [];
const parseItem = <T>(response: ApiResponse<T>): T => response.data as T;

export const useLLMProviders = () => {
  return useQuery({
    queryKey: llmCatalogKeys.providers,
    queryFn: async () => {
      const response =
        await axiosInstance.get<ApiResponse<LLMProvider[]>>('/v1/admin/llm-providers');
      return parseList(response.data);
    },
  });
};

export const useLLMModels = (
  filters: {
    providerPublicId?: string;
    usageScope?: LLMModelUsageScope;
    isActive?: boolean;
    all?: boolean;
    page?: number;
    limit?: number;
  } = {}
) => {
  return useQuery({
    queryKey: llmCatalogKeys.models(filters),
    queryFn: async () => {
      const fetchModelsPage = async (page?: number) => {
        const response = await axiosInstance.get<ApiResponse<LLMModel[]>>('/v1/admin/llm-models', {
          params: {
            providerPublicId: filters.providerPublicId,
            usageScope: filters.usageScope,
            isActive: filters.isActive === undefined ? undefined : String(filters.isActive),
            all: filters.all === undefined ? undefined : String(filters.all),
            page,
            limit: filters.limit,
          },
        });
        return response.data;
      };

      const firstResponse = await fetchModelsPage(filters.page);
      const firstPageModels = parseList(firstResponse);

      if (!filters.all) {
        return firstPageModels;
      }

      const modelsByPublicId = new Map(firstPageModels.map((model) => [model.publicId, model]));
      const totalPages = firstResponse.meta?.totalPages;

      if (totalPages && totalPages > 1) {
        for (let page = 2; page <= totalPages; page += 1) {
          const pageModels = parseList(await fetchModelsPage(page));
          pageModels.forEach((model) => modelsByPublicId.set(model.publicId, model));
        }

        return Array.from(modelsByPublicId.values());
      }

      for (let page = 2; page <= 50; page += 1) {
        const pageModels = parseList(await fetchModelsPage(page));
        const previousSize = modelsByPublicId.size;

        pageModels.forEach((model) => modelsByPublicId.set(model.publicId, model));

        if (pageModels.length === 0 || modelsByPublicId.size === previousSize) {
          break;
        }
      }

      return Array.from(modelsByPublicId.values());
    },
  });
};

export const useLLMModelModalities = () => {
  return useQuery({
    queryKey: llmCatalogKeys.modalities,
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<LLMModelModalityOption[]>>(
        '/v1/common/llm-model-modalities'
      );
      return parseList(response.data);
    },
  });
};

export const useLLMTaskTypes = () => {
  return useQuery({
    queryKey: llmCatalogKeys.taskTypes,
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<LLMTaskTypeOption[]>>(
        '/v1/common/llm-task-types'
      );
      return parseList(response.data);
    },
  });
};

export const useCreateLLMProvider = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (input: LLMProviderFormValues) => {
      const response = await axiosInstance.post<ApiResponse<LLMProvider>>(
        '/v1/admin/llm-providers',
        input
      );
      return parseItem(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: llmCatalogKeys.providers });
      notification.success('Provider created');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      notification.error(error.response?.data?.message || 'Failed to create provider');
    },
  });
};

export const useUpdateLLMProvider = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async ({ publicId, ...input }: UpdateLLMProviderInput) => {
      const response = await axiosInstance.patch<ApiResponse<LLMProvider>>(
        `/v1/admin/llm-providers/${publicId}`,
        input
      );
      return parseItem(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: llmCatalogKeys.providers });
      notification.success('Provider updated');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      notification.error(error.response?.data?.message || 'Failed to update provider');
    },
  });
};

export const useToggleLLMProviderStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ publicId, isActive }: { publicId: string; isActive: boolean }) => {
      const response = await axiosInstance.patch<ApiResponse<LLMProvider>>(
        `/v1/admin/llm-providers/${publicId}/status`,
        { isActive }
      );
      return parseItem(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: llmCatalogKeys.providers });
      queryClient.invalidateQueries({ queryKey: ['llm-models'] });
    },
  });
};

export const useCreateLLMModel = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (input: LLMModelFormValues) => {
      const response = await axiosInstance.post<ApiResponse<LLMModel>>(
        '/v1/admin/llm-models',
        toModelPayload(input)
      );
      return parseItem(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-models'] });
      queryClient.invalidateQueries({ queryKey: llmCatalogKeys.providers });
      notification.success('Model created');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      notification.error(error.response?.data?.message || 'Failed to create model');
    },
  });
};

export const useUpdateLLMModel = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async ({ publicId, ...input }: UpdateLLMModelInput) => {
      const response = await axiosInstance.patch<ApiResponse<LLMModel>>(
        `/v1/admin/llm-models/${publicId}`,
        toModelPayload(input)
      );
      return parseItem(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-models'] });
      notification.success('Model updated');
    },
    onError: (error: AxiosError<ApiResponse>) => {
      notification.error(error.response?.data?.message || 'Failed to update model');
    },
  });
};

export const useToggleLLMModelStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ publicId, isActive }: { publicId: string; isActive: boolean }) => {
      const response = await axiosInstance.patch<ApiResponse<LLMModel>>(
        `/v1/admin/llm-models/${publicId}/status`,
        { isActive }
      );
      return parseItem(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-models'] });
    },
  });
};

function toModelPayload(input: Partial<LLMModelFormValues>) {
  return {
    ...input,
  };
}

export interface InvocationMethodsMetadataResponse {
  methods: InvocationMethodDefinition[];
  supportedBodyBuilderKeys: string[];
  /** bodyBuilderKey choices for ai_binding / cloudflare_run (native text schema). */
  supportedNativeBodyBuilderKeys?: string[];
  supportedParserKeys: string[];
  providerNativeDefaults?: ProviderNativeDefault[];
}

export const useLLMInvocationMethods = () => {
  return useQuery({
    queryKey: llmCatalogKeys.invocationMethods,
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<InvocationMethodsMetadataResponse>>(
        '/v1/admin/llm-models/invocation-methods'
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const usePricingDimensions = (modelPublicId?: string) => {
  return useQuery({
    queryKey: llmCatalogKeys.pricingDimensions(modelPublicId),
    enabled: !!modelPublicId,
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<{ dimensions: PricingDimension[] }>>(
        `/v1/admin/llm-models/${modelPublicId}/pricing-dimensions`
      );
      return response.data.data?.dimensions ?? [];
    },
  });
};
